import React, {useContext, useEffect, useState} from 'react';
import {Link, useRouteMatch, useHistory} from 'react-router-dom';
import {uid} from 'uid';
import UserContext from '../../../context/UserContext';
import CampaignContext from '../../../context/CampaignContext';
import Picture from '../../../components/Picture';
import {watchCampaignCharacters, updateSheet, updateStatus, updateCharacter, setClaimToken, deleteCharacter} from '../../characterRepository';
import {updateCampaign} from '../../campaignRepository';
import {watchRolls} from '../../rollRepository';
import {CLANS_BY_ID} from '../../data/clans';
import {localized} from '../../data/traits';
import {
  CHARACTER_STATUS,
  getHealthMax,
  getWillpowerMax,
  valueFromClick,
  cycleDamageBox,
  setHumanity,
  adjustStains,
} from '../../character';
import {vtmT, currentLanguage} from '../../i18n';
import {DamageTrack, HumanityTrack, HungerDrops, MiniStepper} from '../SheetParts';
import VtmRollChat from '../VtmRollChat';
import VtmDmRollModal from './VtmDmRollModal';
import VtmDmCheatsheet from './VtmDmCheatsheet';
import VtmCampaignPanel from './VtmCampaignPanel';
import VtmConfirmModal from '../VtmConfirmModal';
import VtmImportModal from './VtmImportModal';
import VtmSceneModal from './VtmSceneModal';
import {useScene, VtmSceneForDm} from '../VtmScene';
import {hideScene} from '../../sceneRepository';
import VtmRollRequestModal from './VtmRollRequestModal';
import {useRollRequest, VtmRollRequestStatus} from '../VtmRollRequest';
import {closeRollRequest} from '../../rollRequestRepository';
import {isRequestComplete} from '../../rollRequest';
import '../../../styles/vtm/sheet.css';
import '../../../styles/vtm/roll.css';
import '../../../styles/vtm/dm.css';

const save = (uid, fields) => updateSheet(uid, fields).catch(e => console.log(e));

/** Bouton qui copie le lien d'attribution d'un personnage repris (jeton créé au premier clic). */
const ClaimLinkButton = ({character, sheetUrl}) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      const token = character.claimToken || uid(24);
      if (!character.claimToken) await setClaimToken(character.uid, token);
      const base = sheetUrl.replace(/\/[^/]+$/, '');
      await navigator.clipboard.writeText(`${window.location.origin}${base}/claim/${character.uid}?t=${token}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.log(e);
    }
  };

  return <button type="button" className="vtmButton" onClick={copy}>{copied ? vtmT('claimLinkCopied') : vtmT('claimLink')}</button>;
};

/** Carte d'un personnage : jauges modifiables par le MJ, accès à la fiche, validation. */
const CoterieCard = ({character, sheetUrl, lng, dmUid, onArchive, onDelete}) => {
  const sheet = character.vtm;
  const healthMax = getHealthMax(sheet);
  const willpowerMax = getWillpowerMax(sheet);
  const isPending = character.status === CHARACTER_STATUS.PENDING;
  const isDraft = character.status === CHARACTER_STATUS.DRAFT;
  const editable = !isDraft;
  const unassigned = character.idUser === dmUid;

  return (
    <article className={`vtmDmCard${isPending ? ' pending' : ''}`}>
      <div className="vtmDmCardHeader">
        <div className="vtmDmCardPortrait"><Picture character={character} /></div>
        <div className="vtmDmCardIdentity">
          <h3 className="vtmDmCardName">{character.name || vtmT('newCharacter')}</h3>
          <span className="vtmDmCardMeta">
            {localized(CLANS_BY_ID[sheet.clan]?.name, lng) || '—'}
            {sheet.concept && ` · ${sheet.concept}`}
          </span>
          {/* Le statut est déjà donné par la section (à valider, coterie, brouillons) */}
          {unassigned && <span className="vtmStatus unassigned">{vtmT('unassigned')}</span>}
        </div>
      </div>

      {editable && (
        <div className="vtmDmTracks">
          <div className="vtmDmTrack">
            <span className="vtmTrackTitle">{vtmT('hunger')}</span>
            <HungerDrops
              hunger={sheet.hunger}
              onDropClick={index => save(character.uid, {hunger: valueFromClick(sheet.hunger, index, 5)})}
              dropLabel={index => `${character.name} · ${vtmT('setHunger', {value: valueFromClick(sheet.hunger, index, 5)})}`}
            />
          </div>
          <div className="vtmDmTrack">
            <span className="vtmTrackTitle">{vtmT('health')}</span>
            <DamageTrack
              max={healthMax}
              track={sheet.health}
              label={vtmT('health')}
              onBoxClick={index => save(character.uid, {health: cycleDamageBox(sheet.health, healthMax, index)})}
              boxLabel={index => `${character.name} · ${vtmT('cycleBox', {track: vtmT('health'), value: index + 1})}`}
            />
          </div>
          <div className="vtmDmTrack">
            <span className="vtmTrackTitle">{vtmT('willpower')}</span>
            <DamageTrack
              max={willpowerMax}
              track={sheet.willpower}
              label={vtmT('willpower')}
              onBoxClick={index => save(character.uid, {willpower: cycleDamageBox(sheet.willpower, willpowerMax, index)})}
              boxLabel={index => `${character.name} · ${vtmT('cycleBox', {track: vtmT('willpower'), value: index + 1})}`}
            />
          </div>
          <div className="vtmDmTrack stacked">
            <span className="vtmTrackTitle">{vtmT('humanity')}</span>
            <HumanityTrack
              humanity={sheet.humanity}
              stains={sheet.stains}
              onBoxClick={index => save(character.uid, setHumanity(valueFromClick(sheet.humanity, index, 10), sheet.stains))}
              boxLabel={index => `${character.name} · ${vtmT('setHumanity', {value: valueFromClick(sheet.humanity, index, 10)})}`}
            />
            <MiniStepper
              label={vtmT('stainsLabel')}
              value={sheet.stains}
              onChange={delta => save(character.uid, {stains: adjustStains(sheet.humanity, sheet.stains, delta)})}
              decrementLabel={`${character.name} · ${vtmT('removeStain')}`}
              incrementLabel={`${character.name} · ${vtmT('addStain')}`}
            />
          </div>
        </div>
      )}

      {(unassigned || isPending) && (
        <div className="vtmDmCardActions">
          {unassigned && <ClaimLinkButton character={character} sheetUrl={sheetUrl} />}
          {isPending && (
            <>
              <button type="button" className="vtmButton primary" onClick={() => updateStatus(character.uid, CHARACTER_STATUS.APPROVED).catch(e => console.log(e))}>
                {vtmT('approve')}
              </button>
              <button type="button" className="vtmButton" onClick={() => updateStatus(character.uid, CHARACTER_STATUS.DRAFT).catch(e => console.log(e))}>
                {vtmT('backToDraft')}
              </button>
            </>
          )}
        </div>
      )}
      {/* Pied de carte discret : la fiche à gauche, archiver / supprimer repliés à droite */}
      <div className="vtmDmCardFooter">
        <Link className="vtmTextButton vtmLinkButton" to={sheetUrl}>{vtmT('openSheet')}</Link>
        <details className="vtmDmCardDanger">
          <summary className="vtmTextButton">{vtmT('manage')}</summary>
          <div className="vtmDmCardDangerActions">
            <button type="button" className="vtmTextButton" onClick={() => onArchive(character)}>{vtmT('archive')}</button>
            <button type="button" className="vtmTextButton danger" onClick={() => onDelete(character)}>{vtmT('deleteForever')}</button>
          </div>
        </details>
      </div>
    </article>
  );
};

const CardSection = ({title, characters, baseUrl, lng, dmUid, onArchive, onDelete}) => (
  characters.length === 0 ? null : (
    <section className="vtmDmSection">
      <h2 className="vtmSectionTitle">{title}</h2>
      <div className="vtmDmGrid">
        {characters.map(character => (
          <CoterieCard key={character.uid} character={character} sheetUrl={`${baseUrl}/${character.uid}`} lng={lng} dmUid={dmUid} onArchive={onArchive} onDelete={onDelete} />
        ))}
      </div>
    </section>
  )
);

const REQUEST_CLOSE_MS = 4000;

// Clés de libellés de la fenêtre de confirmation, selon l'action
const CONFIRM_LABELS = {
  campaign: {title: 'archiveCampaign', message: 'archiveCampaignConfirm', action: 'archive'},
  character: {title: 'archiveCharacter', message: 'archiveCharacterConfirm', action: 'archive'},
  delete: {title: 'deleteCharacter', message: 'deleteCharacterConfirm', action: 'deleteForever'},
};

/** Vue du MJ VtM : coterie en temps réel, validation des fiches, jets du MJ et chat complet. */
const VtmDmPage = () => {
  const lng = currentLanguage();
  const {user} = useContext(UserContext);
  const {campaign, updateCampaign: setCampaignContext} = useContext(CampaignContext);
  const match = useRouteMatch();
  const history = useHistory();
  // {type: 'campaign'}, {type: 'character', character} ou {type: 'delete', character}
  const [confirm, setConfirm] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [rolls, setRolls] = useState([]);
  const [rollOpen, setRollOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [sceneOpen, setSceneOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const isDm = Boolean(campaign.uid) && campaign.idUserDm === user.uid;
  const scene = useScene(isDm ? campaign.uid : null);
  const request = useRollRequest(isDm ? campaign.uid : null);
  // La route est /campaigns/:campaignId/dm : les fiches sont à /campaigns/:campaignId/:characterId
  const baseUrl = match.url.replace(/\/dm\/?$/, '');

  useEffect(() => {
    if (!isDm) return undefined;
    const unsubscribeCharacters = watchCampaignCharacters(campaign.uid, setCharacters, e => console.log(e));
    const unsubscribeRolls = watchRolls(campaign.uid, setRolls);
    return () => {
      unsubscribeCharacters();
      unsubscribeRolls();
    };
  }, [campaign.uid, isDm]);

  // Tout le monde a lancé : la demande se ferme seule, le temps de lire le dernier résultat (il reste dans le chat)
  const requestComplete = isRequestComplete(request, rolls);
  useEffect(() => {
    if (!requestComplete) return undefined;
    const timer = setTimeout(() => closeRollRequest(campaign.uid).catch(e => console.log(e)), REQUEST_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [requestComplete, campaign.uid]);

  if (!campaign.uid) return <p className="vtmPageMessage">{vtmT('loading')}</p>;
  if (!isDm) return <p className="vtmPageMessage">{vtmT('dmOnly')}</p>;

  const renameCampaign = async (name) => {
    try {
      await updateCampaign(campaign.uid, {name});
      setCampaignContext({...campaign, name});
    } catch (e) {
      console.log(e);
    }
  };

  const confirmArchive = async () => {
    const target = confirm;
    setConfirm(null);
    try {
      if (target.type === 'campaign') {
        await updateCampaign(campaign.uid, {active: false});
        history.push('/campaigns');
      } else if (target.type === 'delete') {
        await deleteCharacter(target.character.uid);
      } else {
        await updateCharacter(target.character.uid, {active: false});
      }
    } catch (e) {
      console.log(e);
    }
  };

  const archiveCharacter = (character) => setConfirm({type: 'character', character});
  const removeCharacter = (character) => setConfirm({type: 'delete', character});

  const byName = (a, b) => (a.name || '').localeCompare(b.name || '');
  const pending = characters.filter(item => item.status === CHARACTER_STATUS.PENDING).sort(byName);
  const approved = characters.filter(item => item.status !== CHARACTER_STATUS.PENDING && item.status !== CHARACTER_STATUS.DRAFT).sort(byName);
  const drafts = characters.filter(item => item.status === CHARACTER_STATUS.DRAFT).sort(byName);

  return (
    <div className="vtmPlay">
      <div className="vtmPlaySheet">
        <div className="vtmSheet vtmDm">
          <div className="vtmDmHeader">
            <h1 className="vtmName">{vtmT('dmTitle')}</h1>
            <div className="vtmDmCampaignLine">
              <p className="vtmConcept">{campaign.name}</p>
              <button type="button" className="vtmHeaderAction" onClick={() => setCampaignOpen(true)}>{vtmT('campaignPanel')}</button>
            </div>
          </div>
          <VtmDmCheatsheet lng={lng} />
          {characters.length === 0 && <p className="vtmMuted">{vtmT('noCharacters')}</p>}
          <CardSection title={vtmT('pendingApproval')} characters={pending} baseUrl={baseUrl} lng={lng} dmUid={campaign.idUserDm} onArchive={archiveCharacter} onDelete={removeCharacter} />
          <CardSection title={vtmT('coterie')} characters={approved} baseUrl={baseUrl} lng={lng} dmUid={campaign.idUserDm} onArchive={archiveCharacter} onDelete={removeCharacter} />
          <CardSection title={vtmT('drafts')} characters={drafts} baseUrl={baseUrl} lng={lng} dmUid={campaign.idUserDm} onArchive={archiveCharacter} onDelete={removeCharacter} />
          {/* Reprise de personnages : action rare, en pied de page */}
          <div className="vtmDmFooter">
            <button type="button" className="vtmTextButton" onClick={() => setImportOpen(true)}>{vtmT('importCharacters')}</button>
          </div>
        </div>
      </div>
      <aside className="vtmPlayChat">
        <VtmSceneForDm scene={scene} onStop={() => hideScene(campaign.uid).catch(e => console.log(e))} />
        <div className="vtmQuickActions">
          <button type="button" className="vtmButton primary" onClick={() => setRollOpen(true)}>{vtmT('dmRoll')}</button>
          <button type="button" className="vtmButton" onClick={() => setRequestOpen(true)}>{vtmT('rollRequest')}</button>
          <button type="button" className="vtmButton" onClick={() => setSceneOpen(true)}>{vtmT('sceneShow')}</button>
        </div>
        {request && (
          <VtmRollRequestStatus
            request={request}
            rolls={rolls}
            characters={characters}
            onClose={() => closeRollRequest(campaign.uid).catch(e => console.log(e))}
          />
        )}
        <VtmRollChat rolls={rolls} userUid={user.uid} dmUid={campaign.idUserDm} />
      </aside>
      <VtmDmRollModal isOpen={rollOpen} onClose={() => setRollOpen(false)} campaignUid={campaign.uid} user={user} />
      <VtmCampaignPanel
        isOpen={campaignOpen}
        onClose={() => setCampaignOpen(false)}
        campaign={campaign}
        onRename={renameCampaign}
        onArchive={() => setConfirm({type: 'campaign'})}
      />
      <VtmSceneModal
        isOpen={sceneOpen}
        onClose={() => setSceneOpen(false)}
        campaignUid={campaign.uid}
        characters={approved}
        scene={scene}
      />
      <VtmRollRequestModal
        isOpen={requestOpen}
        onClose={() => setRequestOpen(false)}
        campaignUid={campaign.uid}
        characters={approved}
      />
      <VtmImportModal isOpen={importOpen} onClose={() => setImportOpen(false)} campaignUid={campaign.uid} dmUid={campaign.idUserDm} />
      <VtmConfirmModal
        isOpen={Boolean(confirm)}
        title={vtmT(CONFIRM_LABELS[confirm?.type || 'character'].title)}
        message={vtmT(CONFIRM_LABELS[confirm?.type || 'character'].message, {name: confirm?.character?.name || vtmT('newCharacter')})}
        confirmLabel={vtmT(CONFIRM_LABELS[confirm?.type || 'character'].action)}
        onConfirm={confirmArchive}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default VtmDmPage;
